import { useForm } from 'react-hook-form';

import { Camera } from 'types/camera';

import { useCreateReferencePipelines } from 'cameras/hooks/useCreateReferencePipelines';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useUpdateCamera } from 'cameras/hooks/useUpdateCamera';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Drawer } from 'components/Drawer/DrawerDeprecated';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormErrorMessage';
import { Heading } from 'components/Heading/Heading';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { Input } from 'components/Input/Input';
import {
  deploymentFormDrawerClassName,
  NewDeploymentForm,
} from 'deployments/components/Form';
import { Text } from 'components/Text/Text';

import { UniversalBridgePipelines } from './UniversalBridgePipelines';
import { useToggle } from '@mantine/hooks';
import { Deployment } from 'types/deployment';
import { useCreateReferenceDeployments } from 'cameras/hooks/useCreateReferenceDeployments';
import { Separator } from 'components/Separator/Separator';

type UniversalBridgeSettingsFieldValues = {
  external_id: string | null;
};

type UniversalBridgeSettingsProps = {
  camera: Camera;
  allowedStreamIds: string[];
};

export function UniversalBridgeSettings({
  camera,
  allowedStreamIds,
}: UniversalBridgeSettingsProps) {
  const { isRingPlan } = useCurrentPlan();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<UniversalBridgeSettingsFieldValues>({
    defaultValues: {
      external_id: camera.external_id,
    },
  });

  const [isAddingReferencePipeline, toggleIsAddingReferencePipeline] =
    useToggle();

  const {
    mutate: createReferencePipelines,
    isLoading: isCreatingReferencePipelines,
  } = useCreateReferencePipelines({
    onSuccess() {
      //reset({ reference_pipeline_ids: [] });
    },
  });

  const {
    mutate: createReferenceDeployments,
    isLoading: isCreatingReferenceDeployments,
  } = useCreateReferenceDeployments({
    onSuccess() {
      //reset({ reference_pipeline_ids: [] });
    },
  });

  const {
    mutate: updateCamera,
    isLoading: isUpdatingCamera,
    isSuccess: didUpdateCamera,
    error: updateError,
  } = useUpdateCamera({
    onSuccess({ external_id }) {
      reset({ external_id });
    },
  });

  const isSuccess = didUpdateCamera;
  const isLoading =
    isUpdatingCamera ||
    isCreatingReferencePipelines ||
    isCreatingReferenceDeployments;
  const error = updateError;

  function onSubmit({ external_id }: UniversalBridgeSettingsFieldValues) {
    updateCamera({ id: camera.id, external_id: external_id || null });
  }

  function addReferencePipelineAndDeployment(deployment: Deployment) {
    createReferencePipelines({
      camera_id: camera.id,
      pipeline_ids: [deployment.pipeline_id],
    });
    createReferenceDeployments({
      camera_id: camera.id,
      deployment_ids: [deployment.id],
    });
  }

  return (
    <section>
      {!isRingPlan && (
        <>
          <Heading level="3">
            Virtual Camera Settings <br />
            <Text type="paragraph">
              <ExternalLink href="https://docs.lumeo.com/docs/universal-bridge">
                Virtual Camera docs
              </ExternalLink>
            </Text>
          </Heading>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Field label="External source identifier">
              <Input {...register('external_id')} />
              <Text>
                Lumeo will associate uploaded clips with this camera using this
                identifier.
              </Text>
            </Field>

            <FormErrorMessage error={error} />

            <ButtonGroup>
              <Button
                variant="primary"
                type="submit"
                size="small"
                loading={isLoading}
              >
                Save Virtual Camera settings
              </Button>

              {isDirty && (
                <InlineNotification>Unsaved changes</InlineNotification>
              )}

              {isSuccess && !isDirty && (
                <InlineNotification intent="success">Saved!</InlineNotification>
              )}
            </ButtonGroup>
          </form>

          <Separator />
        </>
      )}

      <Heading level="4">
        Workflows <br />
        <Text type="paragraph">
          Lumeo will deploy these workflows for each new uploaded clip
          associated with this camera.
        </Text>
      </Heading>
      <Button
        onClick={() => toggleIsAddingReferencePipeline()}
        variant="primary"
        size="small"
      >
        Configure new workflow
      </Button>

      <div className="h-4" />

      <UniversalBridgePipelines camera={camera} />

      <Drawer
        title="Configure a Workflow"
        header={
          <Text type="paragraph">
            Select a Workflow and then select an existing clip uploaded by this
            camera to configure and test. <br />
            This Workflow will be deployed for each new clip uploaded by this
            camera using settings below. <br />
          </Text>
        }
        containerClassName={deploymentFormDrawerClassName}
        onClose={toggleIsAddingReferencePipeline}
        open={isAddingReferencePipeline}
      >
        <NewDeploymentForm
          onSuccess={(deployment) => {
            addReferencePipelineAndDeployment(deployment);
            toggleIsAddingReferencePipeline(false);
          }}
          nameTemplate={`Reference - ${camera.name} - {{pipeline_name}}`}
          allowedStreamIds={allowedStreamIds}
          allowedCameraIds={[camera.id]}
          canStartOnDeploy={!isRingPlan}
        />
      </Drawer>
    </section>
  );
}
